import { loadQuery, type PreloadedQuery } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { extractResourceStatePromise } from '@jira/router-resources__assets-admin-settings/src/extractResourceStatePromise.tsx';
import DataManagerAdvisoryNotificationsQuery, {
	type DataManagerAdvisoryNotificationsQuery as DataManagerAdvisoryNotificationsQueryType,
} from '@jira/assets-app__data-manager-advisory-notifications/src/__generated__/DataManagerAdvisoryNotificationsQuery.graphql';
import {
	createResource,
	getResourceStore,
	type ResourceStoreContext,
	type RouteResource,
	type RouterDataContext,
} from '@atlassian/react-resource-router';

// Single source for the Data Manager route check — every DM route name is prefixed
// `assets-data-manager-`. The layout mounts the banner unconditionally and relies on this resource
// yielding no query reference on other routes, so the predicate lives only here.
export const isDataManagerRoute = (routeName: string | undefined): boolean =>
	typeof routeName === 'string' && routeName.startsWith('assets-data-manager-');

// Preloads the query at route resolution (off the critical path). workspaceId is read from the
// already-resolved workspaceResource in the store, not re-fetched. Browser-only: DM pages are
// shell-only SSR.
export const dataManagerAdvisoryNotificationsResource: RouteResource<PreloadedQuery<DataManagerAdvisoryNotificationsQueryType> | null> =
	createResource<PreloadedQuery<DataManagerAdvisoryNotificationsQueryType> | null>({
		type: 'ASSETS_DM_ADVISORY_NOTIFICATIONS',
		// Key by route group, not a constant — the resource runs on every Assets route, so a constant
		// key lets the `null` cached on a non-DM route be reused after an SPA transition into a DM route
		// (banner never appears). One key per group: fetched once across DM routes, null elsewhere.
		getKey: ({ route }) => (isDataManagerRoute(route.name) ? 'data-manager' : 'other'),
		isBrowserOnly: true,
		maxAge: 5 * 60 * 1000,
		getData: async (routerContext: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
			if (!isDataManagerRoute(routerContext.route.name)) {
				return null;
			}

			const { storeState } = getResourceStore();
			const workspaceId = await extractResourceStatePromise(storeState.getState().data);
			if (typeof workspaceId !== 'string' || workspaceId === '') {
				return null;
			}

			return loadQuery<DataManagerAdvisoryNotificationsQueryType>(
				getRelayEnvironment(),
				DataManagerAdvisoryNotificationsQuery,
				{ cloudId: tenantContext.cloudId, workspaceId },
				{ fetchPolicy: 'store-or-network' },
			);
		},
	});
